import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function ArticleVeganBakeries() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-vegan-bakeries-vancouver";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "To Live For Bakery & Cafe",
    "section-2": "Edible Flours",
    "section-3": "Bonus Bakery",
    "section-4": "Level V Bakery",
    "section-5": "The Gluten Free Epicurean",
    "section-6": "Sweet Cherubim",
    "section-7": "Beaucoup Bakery (Vegan-Friendly)",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://www.happycow.net",
      label: "HappyCow – Vegan & Vegetarian Restaurants",
    },
    {
      url: "https://www.destinationvancouver.com",
      label: "Destination Vancouver – Official Tourism Board",
    },
    {
      url: "https://www.veggiesabroad.com",
      label: "Veggies Abroad – Vegan Travel Blog",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // Provide as many locations as needed. Each location's "id" must match the
  // ref you use in the corresponding <h3> or <p> section. Coordinates are approximate.
  const locations = [
    {
      id: "section-1",
      lat: 49.2767,
      lng: -123.0556,
      popupText: "To Live For Bakery & Cafe",
    },
    {
      id: "section-2",
      lat: 49.2642,
      lng: -123.1617,
      popupText: "Edible Flours",
    },
    {
      id: "section-3",
      lat: 49.2877,
      lng: -123.1150,
      popupText: "Bonus Bakery",
    },
    {
      id: "section-4",
      lat: 49.2598,
      lng: -123.1000,
      popupText: "Level V Bakery",
    },
    {
      id: "section-5",
      lat: 49.2568,
      lng: -123.0910,
      popupText: "The Gluten Free Epicurean",
    },
    {
      id: "section-6",
      lat: 49.2768,
      lng: -123.0700,
      popupText: "Sweet Cherubim",
    },
    {
      id: "section-7",
      lat: 49.2680,
      lng: -123.1360,
      popupText: "Beaucoup Bakery (Vegan-Friendly)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Vegan Bakeries in Vancouver</title>
        <meta
          name="description"
          content="Discover the top vegan bakeries in Vancouver, including fully vegan spots and those with strong vegan options, plus locations, hours, and insider tips."
        />
        <meta
          name="keywords"
          content="vegan bakeries, Vancouver, plant-based, desserts"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-vegan-bakeries-vancouver.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Vegan Bakeries in Vancouver"
        />
        <meta
          property="og:description"
          content="A practical guide to Vancouver's vegan bakeries, from fully vegan spots to hidden gems offering strong vegan options."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-vegan-bakeries-vancouver.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-vegan-bakeries-vancouver.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Vegan Bakeries in Vancouver"
        />
        <meta
          name="twitter:description"
          content="A thorough guide to plant-based bakeries in Vancouver, including addresses, hours, highlights, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-vegan-bakeries-vancouver.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={
                ref_data?.image ||
                "https://bcouver.com/assets/images/article-fallback.jpg"
              }
              alt="Selection of vegan bakery treats"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              Vancouver’s vegan bakery scene is flourishing, offering a diverse
              range of sweet and savory treats for plant-based eaters. Whether
              you crave a flaky croissant, a mouthwatering donut, or a slice of
              rich chocolate cake, this guide will help you discover the best
              vegan bakeries in town. We’ve gathered comprehensive details:
              addresses, operating hours, standout items, and even approximate
              costs—everything you need for a truly delicious experience!
            </p>

            {/* SECTION: To Live For Bakery & Cafe */}
            <h2 id="section-1">To Live For Bakery & Cafe</h2>
            <p>
              Formerly known for its exceptional banana bread, “To Live For
              Bakery & Cafe” has grown into a fully vegan destination. Enjoy
              tasty pastries, specialty cakes, and a variety of espresso-based
              drinks in a bright, modern setting.
            </p>
            <p>
              <strong>Address:</strong> 1508 Nanaimo St, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-1", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2767}
                coordX={-123.0556}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> 7:00 AM – 5:00 PM (Open daily)
              </li>
              <li>
                <strong>Price Range:</strong> Mid-range for pastries; premium for
                custom cakes
              </li>
              <li>
                <strong>Dietary Options:</strong> 100% vegan, some gluten-free
                pastries (ask for availability)
              </li>
              <li>
                <strong>Seating:</strong> Yes, cafe seating with a modern
                ambiance
              </li>
              <li>
                <strong>Standout Items:</strong> Banana bread, sausage rolls,
                Nanaimo bars, cupcakes
              </li>
            </ul>

            {/* SECTION: Edible Flours */}
            <h2 id="section-2">Edible Flours</h2>
            <p>
              Located in Kitsilano, Edible Flours focuses on creating
              deliciously wholesome baked goods. They offer a wide variety of
              vegan treats, including many gluten-free, wheat-free, and even
              sugar-free specialties.
            </p>
            <p>
              <strong>Address:</strong> 2280 West Broadway, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-2", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2642}
                coordX={-123.1617}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Tue–Sat 8:30 AM – 5:00 PM; Sun
                10:30 AM – 5:00 PM (Closed Monday)
              </li>
              <li>
                <strong>Price Range:</strong> Mid-range; \$3–\$6 for most items
              </li>
              <li>
                <strong>Dietary Options:</strong> 100% vegan, many GF and
                sugar-free choices
              </li>
              <li>
                <strong>Seating:</strong> Limited; mostly grab-and-go
              </li>
              <li>
                <strong>Standout Items:</strong> Cookies, brownies, cupcakes,
                savory rolls, custom cakes
              </li>
            </ul>

            {/* SECTION: Bonus Bakery */}
            <h2 id="section-3">Bonus Bakery</h2>
            <p>
              Tucked into downtown Vancouver, Bonus Bakery offers a bright,
              modern cafe space with whimsical treats like stuffed cookies and
              over-the-top donuts. It’s a popular go-to for office workers and
              students looking for a sweet pick-me-up.
            </p>
            <p>
              <strong>Address:</strong> 401 Burrard St, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-3", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2877}
                coordX={-123.1150}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Mon–Fri 8:00 AM – 5:00 PM
                (Closed weekends)
              </li>
              <li>
                <strong>Price Range:</strong> Mid-range; \$4–\$7 for cookies and
                pastries
              </li>
              <li>
                <strong>Dietary Options:</strong> 100% plant-based, nut-free
                facility, some gluten-free options
              </li>
              <li>
                <strong>Seating:</strong> Yes, indoor cafe seating
              </li>
              <li>
                <strong>Standout Items:</strong> Stuffed cookies, pizza pretzels,
                cinnamon rolls, seasonal donuts
              </li>
            </ul>

            {/* SECTION: Level V Bakery */}
            <h2 id="section-4">Level V Bakery</h2>
            <p>
              Known for its beautiful cakes and elegant pastries, Level V Bakery
              is a favorite for special occasions. Their modern space often
              shares a seating area with a matcha tea shop, making it a cozy
              spot to relax with a sweet treat.
            </p>
            <p>
              <strong>Address:</strong> 39 Kingsway, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-4", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2598}
                coordX={-123.1000}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Daily 9:00 AM – 6:00 PM
              </li>
              <li>
                <strong>Price Range:</strong> Mid-range to premium (specialty
                cakes)
              </li>
              <li>
                <strong>Dietary Options:</strong> 100% vegan, custom gluten-free
                by request
              </li>
              <li>
                <strong>Seating:</strong> Limited cafe seating shared with matcha
                shop
              </li>
              <li>
                <strong>Standout Items:</strong> Custom cakes (tiramisu, earl
                grey, chocolate peanut butter), croissants, macarons
              </li>
            </ul>

            {/* SECTION: The Gluten Free Epicurean */}
            <h2 id="section-5">The Gluten Free Epicurean</h2>
            <p>
              A haven for gluten-intolerant treat-seekers, The Gluten Free
              Epicurean is a fully GF bakery with many vegan offerings. From
              cinnamon buns to pizza buns, their selection keeps expanding and
              rotating, so there’s always something new to try.
            </p>
            <p>
              <strong>Address:</strong> 633 E 15th Ave, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-5", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2568}
                coordX={-123.0910}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Tue–Sat 9:00 AM – 5:00 PM
                (Closed Sun & Mon)
              </li>
              <li>
                <strong>Price Range:</strong> Mid-range; \$4–\$7 typically
              </li>
              <li>
                <strong>Dietary Options:</strong> 100% GF, many vegan items
                available
              </li>
              <li>
                <strong>Seating:</strong> Mostly takeout with minimal seating
              </li>
              <li>
                <strong>Standout Items:</strong> Vegan cinnamon buns, pizza
                buns, brownies, scones
              </li>
            </ul>

            {/* SECTION: Sweet Cherubim */}
            <h2 id="section-6">Sweet Cherubim</h2>
            <p>
              A hidden gem on Commercial Drive, Sweet Cherubim combines a
              vegetarian cafe, natural foods store, and bakery. While not
              exclusively vegan, nearly all items are animal-product free and
              labeled clearly. Their affordable prices and variety of raw,
              gluten-free, and Indian-inspired treats make this a must-visit.
            </p>
            <p>
              <strong>Address:</strong> 1105 Commercial Dr, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-6", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2768}
                coordX={-123.0700}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Mon–Sat 10:00 AM – 8:00 PM; Sun
                11:00 AM – 7:00 PM
              </li>
              <li>
                <strong>Price Range:</strong> Budget-friendly; many items under
                \$4
              </li>
              <li>
                <strong>Dietary Options:</strong> Vegetarian with almost all
                items vegan, GF and nut-free options available
              </li>
              <li>
                <strong>Seating:</strong> Yes, cafe seating in an attached
                restaurant
              </li>
              <li>
                <strong>Standout Items:</strong> Carrot cake, raw vegan
                cheesecakes, date squares, Indian sweets
              </li>
            </ul>

            {/* SECTION: Beaucoup Bakery (Vegan-Friendly) */}
            <h2 id="section-7">Beaucoup Bakery (Vegan-Friendly)</h2>
            <p>
              Known primarily as a French-inspired bakery, Beaucoup has
              answered the call for plant-based treats by introducing a
              dedicated vegan pastry line. The selection changes seasonally but
              often includes indulgent cookies, bonbons, and croissant-style
              pastries.
            </p>
            <p>
              <strong>Address:</strong> 2150 Fir St, Vancouver, BC
            </p>
            <p
              ref={(el) => setSpotRef("section-7", el)}
              style={{ scrollMarginTop: "80px" }}
            >
              <strong>Coordinates:</strong>{" "}
              <Marker
                coordY={49.2680}
                coordX={-123.1360}
                mobileMapRef={mobileMapRef}
                mapRef={mapRef}
              />
            </p>
            <ul>
              <li>
                <strong>Operating Hours:</strong> Daily 8:00 AM – 5:00 PM
              </li>
              <li>
                <strong>Price Range:</strong> Premium (French patisserie style)
              </li>
              <li>
                <strong>Dietary Options:</strong> Not exclusively vegan, but
                offers a dedicated vegan pastry line
              </li>
              <li>
                <strong>Seating:</strong> Yes, cafe with indoor seating
              </li>
              <li>
                <strong>Standout Items:</strong> Vegan “cruffin,” PB Squared
                cookie, chocolate bonbons
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              Vancouver’s vegan bakeries are subject to the same food safety
              guidelines as any bakery. The city encourages sustainable, ethical
              practices, and many businesses follow the ban on single-use
              plastics, opting for compostable packaging. There is no special
              government license for “vegan” labeling, but it’s standard to
              disclose all ingredients and potential allergens. Look for clear
              allergen labeling for nuts, gluten, or soy, and feel free to ask
              staff about any concerns.
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. Are these bakeries 100% vegan?</h3>
              <p>
                Most listed here are fully vegan. However, a few like Beaucoup
                Bakery offer a vegan pastry line in addition to non-vegan items.
                When in doubt, look for the “vegan” label or ask staff.
              </p>

              <h3>2. Which are gluten-free or nut-free?</h3>
              <p>
                Several spots have gluten-free options (Edible Flours, Bonus
                Bakery, To Live For) or are entirely gluten-free (The Gluten
                Free Epicurean). Bonus Bakery is also a nut-free facility.
              </p>

              <h3>3. How can I be sure they won’t sell out?</h3>
              <p>
                Some of the smaller bakeries do sell out of popular items.
                Arrive early, especially on weekends. If you want something
                specific like a custom cake, call or order online in advance.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              From cozy grab-and-go spots to chic French-inspired cafes,
              Vancouver’s vegan bakery scene is diverse and delicious. Whether
              you’re gluten-free, nut-free, or simply curious about plant-based
              delights, these bakeries showcase the city’s commitment to
              sustainability, inclusivity, and fantastic flavors. Be sure to
              explore multiple spots—your taste buds will thank you!
            </p>
            <p>
              <em>
                Disclaimer: Always double-check hours and menu items before
                visiting, and inform staff if you have severe allergies. Stay
                updated with local guidelines and enjoy responsibly.
              </em>
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}